import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { collectionDetailPath } from "../../utils/routes";
import styles from "./Collections.module.css";
import { post } from "../../utils/request";
import {
  getFilePreviewUrls,
  getCachedPreviewUrl,
} from "../../utils/attachmentPreview";
import { t } from "../../utils/i18n";
import { stripHtml } from "../../utils/stripHtml";

const PAGE_SIZE = 9;
// 合集列表接口
const COLLECTIONS_API = "/game/collection/collection-list";
// 合集搜索接口
const COLLECTION_SEARCH_API = "/game/collection/collection-search";

const mapCollectionItem = item => {
  // 同时兼容列表接口和搜索接口的字段名
  const id =
    item.id ||
    item.collectionId ||
    item.code ||
    item.collectionCode ||
    item.name;
  const name = item.name || item.collectionName || "";
  const description =
    item.description ||
    item.collectionIntro ||
    item.summary ||
    item.introduction ||
    "";
  // 列表里自带的封面链接（如果有的话）
  const coverFromData =
    item.coverUrl ||
    item.cover ||
    item.coverImage ||
    item.imageUrl ||
    (Array.isArray(item.materialInfoList) &&
      item.materialInfoList[0]?.fileUrl) ||
    "";
  // 用于调用 /api/file/preview 的附件 id，统一字符串便于缓存命中
  const iconId =
    item.collectionIcon != null && item.collectionIcon !== ""
      ? String(item.collectionIcon)
      : "";
  const gameSize = item.gameSize || "";
  const likeCount = item.likeCount || "";

  return {
    id,
    name,
    description,
    cover: getCachedPreviewUrl(iconId) || coverFromData,
    gameSize,
    likeCount,
    iconId,
  };
};

const Collections = ({ searchKeyword = "" }) => {
  const [collections, setCollections] = useState([]);
  const [pageNo, setPageNo] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const initialLoadRef = useRef(false);
  const navigate = useNavigate();

  // 调用合集搜索接口
  const fetchCollectionSearch = useCallback(async keyword => {
    setLoading(true);
    try {
      const res = await post(COLLECTION_SEARCH_API, {
        collectionName: keyword,
      });
      // 搜索接口直接返回 data 数组
      const rawList = Array.isArray(res?.data) ? res.data : [];
      const list = rawList.map(mapCollectionItem).filter(c => c.id);
      setCollections(list);

      // 批量拉取封面（去重 iconId，一次 setState），避免 N 次请求 + N 次 setState
      const ids = [...new Set(list.map(c => c.iconId).filter(Boolean))];
      if (ids.length > 0) {
        getFilePreviewUrls(ids)
          .then(urls => {
            const idToUrl = new Map();
            ids.forEach((id, i) => {
              if (urls[i]) idToUrl.set(id, urls[i]);
            });
            setCollections(prev =>
              prev.map(c => ({
                ...c,
                cover: c.cover || (c.iconId ? idToUrl.get(c.iconId) || "" : ""),
              })),
            );
          })
          .catch(() => {});
      }
      setHasMore(false); // 搜索结果一次性展示，不分页
    } catch (err) {
      console.warn("合集搜索失败:", err);
      setCollections([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // 调用合集分页列表接口
  const fetchCollections = useCallback(
    async page => {
      if (loading || !hasMore) return;
      setLoading(true);
      try {
        const res = await post(COLLECTIONS_API, {
          data: {},
          pageNo: page,
          pageSize: PAGE_SIZE,
          sortingFieldList: [{ field: "", order: "" }],
        });

        const rawList =
          (Array.isArray(res?.data?.records) && res.data.records) ||
          (Array.isArray(res?.data?.list) && res.data.list) ||
          (Array.isArray(res?.data) && res.data) ||
          [];

        const list = rawList.map(mapCollectionItem).filter(c => c.id);

        setCollections(prev => (page === 1 ? list : [...prev, ...list]));

        const total =
          typeof res?.total === "number"
            ? res.total
            : typeof res?.data?.total === "number"
              ? res.data.total
              : undefined;

        if (total != null) {
          const loaded = (page - 1) * PAGE_SIZE + list.length;
          setHasMore(loaded < total);
        } else {
          setHasMore(list.length >= PAGE_SIZE);
        }

        setPageNo(page);
        if (page === 1) initialLoadRef.current = false;

        // 批量拉取封面（去重 iconId），每页只一次 setState，避免 8 条发 8+ 次请求
        const ids = [...new Set(list.map(c => c.iconId).filter(Boolean))];
        if (ids.length === 0) return;

        getFilePreviewUrls(ids)
          .then(urls => {
            const idToUrl = new Map();
            ids.forEach((id, i) => {
              if (urls[i]) idToUrl.set(id, urls[i]);
            });
            setCollections(prev =>
              prev.map(c => ({
                ...c,
                cover: c.cover || (c.iconId ? idToUrl.get(c.iconId) || "" : ""),
              })),
            );
          })
          .catch(() => {});
      } catch (err) {
        console.warn("获取合集列表失败:", err);
        // 查询异常时标记为没有更多，避免一直重复请求
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [loading, hasMore],
  );

  // searchKeyword 变化时：有值调搜索接口，无值重置并加载第一页列表
  useEffect(() => {
    if (searchKeyword.trim()) {
      initialLoadRef.current = false;
      fetchCollectionSearch(searchKeyword.trim());
    } else {
      // 防止 Strict Mode 或依赖抖动导致重复请求第一页（8 张图却请求 26 次）
      if (initialLoadRef.current) return;
      initialLoadRef.current = true;
      setCollections([]);
      setHasMore(true);
      setPageNo(1);
      fetchCollections(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKeyword]);

  const handleOpenDetail = col => {
    if (!col?.id) return;
    navigate(collectionDetailPath(col.id));
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      const distanceToBottom = scrollHeight - scrollTop - clientHeight;
      if (distanceToBottom < 120 && hasMore && !loading) {
        fetchCollections(pageNo + 1);
      }
    };

    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [pageNo, hasMore, loading, fetchCollections]);

  return (
    <div className={styles.collectionsPage} ref={scrollRef}>
      <h2 className={styles.title}>{t("collections.title", "Collections")}</h2>
      <p className={styles.subtitle}>
        {t("collections.subtitle", "Discover more selected collections")}
      </p>

      <div className={styles.grid}>
        {collections.map(col => (
          <div
            key={col.id}
            className={styles.card}
            onClick={() => handleOpenDetail(col)}
          >
            <div className={styles.coverWrapper}>
              {col.cover ? (
                <img src={col.cover} alt={col.name} className={styles.cover} />
              ) : col.iconId ? null : (
                <div className={styles.coverPlaceholder}>
                  <span className={styles.coverPlaceholderText}>
                    {col.name?.charAt(0) || "C"}
                  </span>
                </div>
              )}
            </div>
            <div className={styles.cardBody}>
              <div className={styles.cardTitle} title={col.name}>
                {col.name}
              </div>
              {col.description && (
                <div
                  className={styles.cardDesc}
                  title={stripHtml(col.description)}
                >
                  <div
                    className={styles.cardDescHtml}
                    dangerouslySetInnerHTML={{
                      __html: String(col.description),
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {loading && (
        <div className={styles.loadingMore}>{t("common.loading", "Loading...")}</div>
      )}
      {!hasMore && collections.length > 0 && (
        <div className={styles.noMore}>
          {t("collections.noMore", "No more collections")}
        </div>
      )}
    </div>
  );
};

export default Collections;
